import React from 'react';
import { PortfolioDetailsInvestorItem } from '../../../../models/Portfolio';
import { Box, ITheme, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface IInvestorsAgeGraphProps {
    investors: PortfolioDetailsInvestorItem[];
}


const renderCategoryLabel = (pos: number[], range: string, textColor: string): React.ReactNode => {
    return <foreignObject width={"30px"} height={"30px"} x={pos[0] - 15} y={pos[1] - 15}>
        <Box width={"100%"} height={"100%"} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                <Typography fontSize={"8px"} fontWeight={"400"} lineHeight={"9.75px"} color={textColor}>{range}</Typography>
        </Box>
    </foreignObject>
}
const renderAxisLabel = (pos: number[], text: string, textColor: string, index: number): React.ReactNode => {
    return <foreignObject width={"30px"} height={"30px"} x={pos[0] - 15} y={pos[1] - 15} key={`axis-label-${index}`}>
        <Box width={"100%"} height={"100%"} display={"flex"} justifyContent={"flex-end"} alignItems={"center"}>
            <Typography fontSize={"8px"} fontWeight={"400"} lineHeight={"9.75px"} textAlign={"right"} color={textColor}>{text}</Typography>
        </Box>
    </foreignObject>
}

const InvestorsAgeGraph: React.FC<IInvestorsAgeGraphProps> = ({ investors }) => {
    const { t } = useTranslation();
    const theme = useTheme() as ITheme;

    const width = 200;
    const height = 120;
    const maxHeight = 90; // Highest bar of the graph
    const barWidth = 24; // Horizontal size of the bar
    const barSpacing = 15; // Space between the bars
    /* Calculating the categories values */
    const valueCat1 = investors.filter(i => i.age < 35).length;
    const valueCat2 = investors.filter(i => i.age > 34 && i.age < 45).length;
    const valueCat3 = investors.filter(i => i.age > 44 && i.age < 65).length;
    const valueCat4 = investors.filter(i => i.age > 64).length;
    const total = investors.length;
    let highest = Math.max(valueCat1, valueCat2, valueCat3, valueCat4);

    const searchedMultiple = 5;
    const reste = highest % searchedMultiple; // searching Highest horizontal bar by multiple of 5
    const highestHbar = highest - reste + searchedMultiple; // Highest horizontal bar
    highest = highestHbar;
    const hbarCount = 5 // Number or horizontal bars

    const renderLabel = (pos: number[], proportion: number, textColor: string, backgroundColor: string, index: number): React.ReactNode => {
        return <foreignObject width={"60px"} height={"30px"} x={pos[0] - 30} y={pos[1] - 15} key={`label-${index}`}>
            <Box width={"100%"} height={"100%"} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                <Box display={"flex"} padding={"1px 2px"} minWidth={"28px"} bgcolor={backgroundColor} borderRadius={"10px"} justifyContent={"center"} alignItems={"center"}>
                    <Typography fontSize={"13px"} fontWeight={"400"} lineHeight={"15.85px"} color={textColor}>{(100 * proportion / total).toFixed(0)}%</Typography>
                </Box>
            </Box>
        </foreignObject>
    }

    return <Box display={"flex"} flexDirection={"column"} gap={"13px"} flexGrow={1} flexBasis={"100px"}>
        <Typography>{t('PortfolioDetailsInvestorsDemographicSplitAge')}</Typography>

        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} fill="none" xmlns="http://www.w3.org/2000/svg">
            <g transform={`translate(${25}, ${height - 20})`}>
                {Array.from(Array(hbarCount)).map((a, i) => <path key={`hor-bar-${i}`} d={`M0 -${((highestHbar * (i + 1) / hbarCount) / highest) * maxHeight} H${barSpacing * 5 + barWidth * 4}`} stroke="#BDBDBD" strokeDasharray="2 2" />)}
                {Array.from(Array(hbarCount+1)).map((a, i) => renderAxisLabel([-18, -((highestHbar * (i) / hbarCount) / highest) * maxHeight],`${Math.floor(highestHbar * (i)/hbarCount)}`, theme.palette.leo.tone14, i))}
                {renderCategoryLabel([barSpacing + barWidth/2, 10], "22-34", theme.palette.leo.tone14)}
                {renderCategoryLabel([barSpacing*2 + barWidth*3/2, 10], "35-44", theme.palette.leo.tone14)}
                {renderCategoryLabel([barSpacing*3 + barWidth*5/2, 10], "45-64", theme.palette.leo.tone14)}
                {renderCategoryLabel([barSpacing*4 + barWidth*7/2, 10], "65+", theme.palette.leo.tone14)}
                <rect x={barSpacing} y={-maxHeight * (valueCat1 / highest)} width={barWidth} height={maxHeight * (valueCat1 / highest) + 1} fill={theme.palette.leo.primary.blue[200]} />
                <rect x={barSpacing * 2 + barWidth} y={-maxHeight * (valueCat2 / highest)} width={barWidth} height={maxHeight * (valueCat2 / highest) + 1} fill={theme.palette.leo.primary.blue[300]} />
                <rect x={barSpacing * 3 + barWidth * 2} y={-maxHeight * (valueCat3 / highest)} width={barWidth} height={maxHeight * (valueCat3 / highest) + 1} fill={theme.palette.leo.primary.blue[500]} />
                <rect x={barSpacing * 4 + barWidth * 3} y={-maxHeight * (valueCat4 / highest)} width={barWidth} height={maxHeight * (valueCat4 / highest) + 1} fill={theme.palette.leo.primary.blue[100]} />
                {(valueCat1 / total) > 0.05 && renderLabel([barSpacing + barWidth / 2, -maxHeight * (valueCat1 / highest) / 2], valueCat1, theme.palette.leo.primary.blue[200], theme.palette.background.paper, 10)}
                {(valueCat2 / total) > 0.05 && renderLabel([barSpacing * 2 + barWidth * 3 / 2, -maxHeight * (valueCat2 / highest) / 2], valueCat2, theme.palette.leo.primary.blue[300], theme.palette.background.paper, 11)}
                {(valueCat3 / total) > 0.05 && renderLabel([barSpacing * 3 + barWidth * 5 / 2, -maxHeight * (valueCat3 / highest) / 2], valueCat3, theme.palette.leo.primary.blue[500], theme.palette.background.paper, 12)}
                {(valueCat4 / total) > 0.00 && renderLabel([barSpacing * 4 + barWidth * 7 / 2, (valueCat4 / total) < 0.2 ? -20 : -maxHeight * (valueCat4 / highest) / 2], valueCat4, theme.palette.leo.primary.blue[100], theme.palette.background.paper, 13)}
            </g>
        </svg>
        <Box display={"flex"} gap={"11px 8px"} flexWrap={"wrap"}>
            {[{ name: "22-34", color: theme.palette.leo.primary.blue[200]},
            { name: "35-44", color: theme.palette.leo.primary.blue[300]},
            { name: "45-64", color: theme.palette.leo.primary.blue[500]},
            { name: "65+", color: theme.palette.leo.primary.blue[100]}].map((i) =>
            <Box display={"flex"} gap={"8px"} alignItems={"center"} key={`label-${i.name}`} flexBasis={"45%"} flexGrow={1}>
                <Box width={"14px"} height={"14px"} bgcolor={i.color} borderRadius={"10px"}></Box>
                <Box display={"flex"} alignItems={"end"} gap={"3px"}>
                    <Typography fontSize={"13px"} fontWeight={"600"} lineHeight={"14px"} color={theme.palette.leo.tone3}>{i.name}</Typography>
                    <Typography fontSize={"9px"} fontWeight={"600"} lineHeight={"9px"} color={theme.palette.leo.tone3}>{t('PortfolioDetailsInvestorsDemographicSplitAgeYearsOld')}</Typography>
                </Box>
            </Box>
            )}
        </Box>
    </Box>;
}

export default InvestorsAgeGraph;